import React, { useState, useEffect } from 'react';
import EmbedCodeBox from "@/components/EmbedCodeBox";
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth, useTheme, useLanguage, API } from '@/App';
import { getTranslation } from '@/translations';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  MessageSquare,
  ArrowLeft,
  Save,
  Code,
  Plus,
  Trash2,
  Copy,
  Check,
  Bot,
  Palette,
  Globe,
  FileText,
  Send,
  Upload,
  File,
  AlertCircle,
  X,
  ShoppingCart,
  Headphones,
  Link2,
  Lightbulb,
  BarChart3,
  ImageIcon
} from 'lucide-react';

const ChatbotDetail = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const { theme } = useTheme();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (key) => getTranslation(language, key);
  
  const [aiAgent, setAiAgent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [generatingEmbed, setGeneratingEmbed] = useState(false);
  // const {
  //   embedCode,
  //   generatingEmbed,
  //   copied,
  //   generateEmbedCode,
  //   copyEmbedCode
  // } = useEmbed();
  
  // Form states
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [agentType, setAgentType] = useState('sales');
  const [marketplaceLinks, setMarketplaceLinks] = useState([]);
  const [systemPrompt, setSystemPrompt] = useState('');
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [botLanguage, setBotLanguage] = useState('id');
  const [primaryColor, setPrimaryColor] = useState('#10B981');
  const [faqs, setFaqs] = useState([]);
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [planLimits, setPlanLimits] = useState(null);
  
  // Chat preview
  const [previewMessages, setPreviewMessages] = useState([]);
  const [previewInput, setPreviewInput] = useState('');
  const [chatting, setChatting] = useState(false);
  const [sessionId, setSessionId] = useState('');

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  // Get or create persistent preview session for this aiAgent
  // Session ID tetap, tapi history tidak dimuat ulang (always fresh start)
  useEffect(() => {
    const storageKey = `aiAgent_preview_session_${id}`;
    const savedSessionId = localStorage.getItem(storageKey);
    
    if (savedSessionId) {
      setSessionId(savedSessionId);
    } else {
      // Generate new persistent session ID
      const newSessionId = `preview_${id}_${Date.now()}`;
      localStorage.setItem(storageKey, newSessionId);
      setSessionId(newSessionId);
    }
  }, [id]);

  useEffect(() => {
    fetchAiAgent();
  }, [id]);

  const fetchAiAgent = async () => {
    try {
      const response = await axios.get(`${API}/chatbots/${id}`, { headers, withCredentials: true });
      const bot = response.data;
      setAiAgent(bot);
      setName(bot.name);
      setDescription(bot.description || '');
      setAgentType(bot.agent_type || 'sales');
      setMarketplaceLinks(bot.marketplace_links || []);
      setSystemPrompt(bot.system_prompt || '');
      setWelcomeMessage(bot.welcome_message || 'Halo! Ada yang bisa saya bantu?');
      setBotLanguage(bot.language || 'id');
      setPrimaryColor(bot.primary_color || '#10B981');
      setFaqs(bot.faqs || []);
      setKnowledgeFiles(bot.knowledge_files || []);
      setEmbedCode(bot.embed_code || '');
      
      // Always start fresh with welcome message (no history loading)
      setPreviewMessages([{ type: 'bot', text: bot.welcome_message || 'Halo! Ada yang bisa saya bantu?' }]);
    } catch (error) {
      toast.error(t('aiAgentDetail.loadFailed'));
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanLimits();
  }, []);

  const fetchPlanLimits = async () => {
    try {
      const response = await axios.get(`${API}/plan/limits`, { headers, withCredentials: true });
      setPlanLimits(response.data);
    } catch (error) {
      console.error('Error fetching plan limits:', error);
    }
  };

  const saveAiAgent = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/chatbots/${id}`, {
        name,
        description,
        agent_type: agentType,
        marketplace_links: agentType === 'cs' ? marketplaceLinks.filter(l => l.url) : [],
        system_prompt: systemPrompt,
        welcome_message: welcomeMessage,
        language: botLanguage,
        primary_color: primaryColor,
        faqs
      }, { headers, withCredentials: true });
      toast.success(t('aiAgentDetail.saveSuccess'));
    } catch (error) {
      toast.error(t('aiAgentDetail.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const generateEmbedCode = async () => {
    setGeneratingEmbed(true);
    try {
      const response = await axios.post(`${API}/chatbots/${id}/generate-embed`, {}, { headers, withCredentials: true });
      setEmbedCode(response.data.embed_code);
      toast.success(t('aiAgentDetail.embedSuccess'));
    } catch (error) {
      toast.error(t('aiAgentDetail.embedFailed'));
    } finally {
      setGeneratingEmbed(false);
    }
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedCode);
    setCopied(true);
    toast.success(t('aiAgentDetail.codeCopied'));
    setTimeout(() => setCopied(false), 2000);
  };

  const addFaq = () => {
    setFaqs([...faqs, { question: '', answer: '' }]);
  };

  const updateFaq = (index, field, value) => {
    const newFaqs = [...faqs];
    newFaqs[index][field] = value;
    setFaqs(newFaqs);
  };

  const removeFaq = (index) => {
    setFaqs(faqs.filter((_, i) => i !== index));
  };

  const sendPreviewMessage = async () => {
    if (!previewInput.trim() || chatting) return;
    
    const userMessage = previewInput;
    setPreviewMessages([...previewMessages, { type: 'user', text: userMessage }]);
    setPreviewInput('');
    setChatting(true);
    
    try {
      const response = await axios.post(`${API}/chat/${id}`, {
        query: userMessage,
        session_id: sessionId || null
      });
      
      // Save session ID if it's new
      if (response.data.session_id && response.data.session_id !== sessionId) {
        setSessionId(response.data.session_id);
        const storageKey = `aiAgent_preview_session_${id}`;
        localStorage.setItem(storageKey, response.data.session_id);
      }
      
      // Include images in the message if available
      setPreviewMessages(prev => [...prev, { 
        type: 'bot', 
        text: response.data.response,
        images: response.data.images || []
      }]);
    } catch (error) {
      // Handle different error types
      let errorMessage = t('aiAgentDetail.errorMessage');
      if (error.response?.status === 403) {
        errorMessage = 'Maaf, limit sudah tercapai. Upgrade plan anda!';
      }
      setPreviewMessages(prev => [...prev, { type: 'bot', text: errorMessage }]);
    } finally {
      setChatting(false);
    }
  };

  const clearChatHistory = () => {
    // Clear localStorage session
    const storageKey = `aiAgent_preview_session_${id}`;
    localStorage.removeItem(storageKey);
    
    // Generate new session ID
    const newSessionId = `preview_${id}_${Date.now()}`;
    localStorage.setItem(storageKey, newSessionId);
    setSessionId(newSessionId);
    
    // Reset messages to welcome message
    setPreviewMessages([{ type: 'bot', text: welcomeMessage || 'Halo! Ada yang bisa saya bantu?' }]);
    
    toast.success(language === 'id' ? 'Riwayat chat dibersihkan' : 'Chat history cleared');
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['pdf', 'docx', 'xlsx', 'xls', 'csv', 'txt'];
    const fileExt = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExt)) {
      toast.error(`Format file tidak didukung. Gunakan: ${allowedTypes.join(', ')}`);
      return;
    }

    // Check file size
    const maxSize = planLimits?.plan === 'trial' ? 2 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const maxSizeMB = planLimits?.plan === 'trial' ? 2 : 5;
      toast.error(`Ukuran file terlalu besar. Maksimal ${maxSizeMB}MB`);
      return;
    }

    // Check file count limit
    const maxFiles = planLimits?.plan === 'trial' ? 1 : 5;
    if (knowledgeFiles.length >= maxFiles) {
      toast.error(`Batas file tercapai. Plan ${planLimits?.plan.toUpperCase()} maksimal ${maxFiles} file`);
      return;
    }

    setUploadingFile(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(
        `${API}/chatbots/${id}/upload-file`,
        formData,
        {
          headers: {
            ...headers,
            'Content-Type': 'multipart/form-data',
          },
          withCredentials: true,
        }
      );

      toast.success('File berhasil di-upload dan diproses!');
      fetchAiAgent(); // Refresh aiAgent data to get updated files
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal upload file';
      toast.error(errorMsg);
    } finally {
      setUploadingFile(false);
      event.target.value = ''; // Reset input
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Yakin ingin menghapus file ini?')) return;

    try {
      await axios.delete(`${API}/chatbots/${id}/files/${fileId}`, {
        headers,
        withCredentials: true,
      });

      toast.success('File berhasil dihapus');
      fetchAiAgent(); // Refresh
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Gagal menghapus file';
      toast.error(errorMsg);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleDeleteChatbot = async () => {
    if (!window.confirm('Yakin ingin menghapus chatbot ini? Semua data percakapan dan file terkait juga akan dihapus. Aksi ini tidak bisa dibatalkan.')) return;
    
    try {
      await axios.delete(`${API}/chatbots/${id}`, { headers, withCredentials: true });
      toast.success('Chatbot berhasil dihapus');
      navigate('/agents');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus chatbot');
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-[#0A0A0A]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#0A0A0A]">
      {/* Header */}
      <header className="sticky top-0 backdrop-blur-xl bg-white/85 dark:bg-[#0A0A0A]/85 border-b border-slate-200 dark:border-zinc-800/80 z-20">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate('/agents')}
              className="shrink-0 px-2 text-slate-500 dark:text-zinc-400 dark:hover:bg-zinc-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex min-w-0 items-center gap-3">
              <div 
                className="hidden sm:grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                style={{ backgroundColor: primaryColor + '20' }}
              >
                <Bot className="w-5 h-5" style={{ color: primaryColor }} />
              </div>
              <div className="min-w-0">
                <h1 className="truncate text-sm sm:text-base font-bold leading-tight text-slate-900 dark:text-zinc-50" style={{ fontFamily: 'Manrope' }}>{name}</h1>
                <div className="mt-0.5 flex items-center gap-2">
                  <span className="truncate text-xs text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.editAiAgent')}</span>
                  <span className="hidden sm:inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300" data-testid="header-agent-type-badge">
                    {agentType === 'cs' ? 'CS Agent' : 'Sales Agent'}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link to={`/analytics/${id}`}>
              <Button variant="outline" size="sm" className="rounded-xl px-2.5 sm:px-4 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800 text-xs sm:text-sm">
                <BarChart3 className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">{t('aiAgentDetail.viewAnalytics')}</span>
              </Button>
            </Link>
            <Button 
              onClick={saveAiAgent} 
              size="sm"
              className="rounded-xl bg-indigo-600 px-3 sm:px-4 text-xs hover:bg-indigo-700 sm:text-sm"
              disabled={saving}
              data-testid="save-aiAgent-btn"
            >
              {saving ? t('aiAgentDetail.saving') : (
                <>
                  <Save className="w-4 h-4 mr-1 sm:mr-2" />
                  {t('aiAgentDetail.save')}
                </>
              )}
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Settings Panel */}
          <div className="space-y-6">
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="mb-6 w-full justify-start gap-1 overflow-x-auto rounded-xl bg-slate-100 p-1 dark:bg-zinc-900">
                {[
                  ['general', t('aiAgentDetail.tabGeneral')],
                  ['prompt', t('aiAgentDetail.tabPrompt')],
                  ['faq', t('aiAgentDetail.tabFaq')],
                  ['knowledge', 'Data'],
                  ['embed', t('aiAgentDetail.tabEmbed')]
                ].map(([value, label]) => (
                  <TabsTrigger
                    key={value}
                    value={value}
                    data-testid={`tab-${value}`}
                    className="flex-1 min-w-[72px] rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors dark:text-zinc-400 data-[state=active]:bg-white data-[state=active]:text-indigo-700 data-[state=active]:shadow-sm dark:data-[state=active]:bg-indigo-500/15 dark:data-[state=active]:text-indigo-300"
                  >
                    {label}
                  </TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="general" className="space-y-6">
                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400 mb-5 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    {t('aiAgentDetail.basicInfo')}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.aiAgentName')}</Label>
                      <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="mt-2 rounded-xl bg-slate-50 dark:bg-zinc-950/60 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                        data-testid="aiAgent-name-input"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.description')}</Label>
                      <Textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        className="mt-2 rounded-xl bg-slate-50 dark:bg-zinc-950/60 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                        rows={3}
                        data-testid="aiAgent-desc-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400 mb-5 flex items-center gap-2">
                    <Bot className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    Tipe AI Agent
                  </h2>
                  <div className="space-y-3">
                    {[
                      { id: 'sales', icon: ShoppingCart, label: 'Sales Agent', desc: 'Handle customer dari awal hingga closing (order, pembayaran, pengiriman)' },
                      { id: 'cs', icon: Headphones, label: 'CS Agent', desc: 'Customer service untuk tanya jawab saja (tanpa flow order)' }
                    ].map((type) => {
                      const Icon = type.icon;
                      return (
                        <div
                          key={type.id}
                          data-testid={`agent-type-option-${type.id}`}
                          className={`group flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition-[border-color,background-color,box-shadow] ${
                            agentType === type.id
                              ? 'border-indigo-400 bg-indigo-50/70 shadow-[0_12px_24px_-16px_rgba(79,70,229,0.6)] dark:border-indigo-500/60 dark:bg-indigo-500/10'
                              : 'border-slate-200 hover:border-indigo-300 dark:border-zinc-800 dark:hover:border-zinc-700'
                          }`}
                          onClick={() => setAgentType(type.id)}
                        >
                          <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl transition-colors ${
                            agentType === type.id
                              ? 'bg-indigo-100 text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-300'
                              : 'bg-slate-100 text-slate-500 dark:bg-zinc-800 dark:text-zinc-400'
                          }`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-slate-900 dark:text-zinc-50">{type.label}</p>
                            <p className="mt-0.5 text-sm leading-relaxed text-slate-500 dark:text-zinc-400">{type.desc}</p>
                          </div>
                          <div className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors ${
                            agentType === type.id
                              ? 'border-indigo-500 bg-indigo-500'
                              : 'border-slate-300 dark:border-zinc-600'
                          }`}>
                            {agentType === type.id && <Check className="h-3 w-3 text-white" />}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {agentType === 'cs' && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-zinc-800" data-testid="marketplace-links-edit">
                      <div className="flex items-center gap-2 mb-3">
                        <Link2 className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                        <Label className="font-semibold dark:text-zinc-50">Link Marketplace</Label>
                      </div>
                      <p className="text-sm text-slate-500 mb-3 dark:text-zinc-400">Customer akan diarahkan ke link ini saat mau beli</p>
                      {marketplaceLinks.map((link, index) => (
                        <div key={index} className="flex gap-2 mb-2">
                          <Select value={link.platform} onValueChange={(val) => {
                            const updated = [...marketplaceLinks];
                            updated[index] = { ...updated[index], platform: val };
                            setMarketplaceLinks(updated);
                          }}>
                            <SelectTrigger className="rounded-xl bg-slate-50 dark:bg-zinc-950/60 border-slate-200 dark:border-zinc-800 w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="dark:bg-zinc-900">
                              {['Shopee', 'Tokopedia', 'TikTok Shop', 'Lazada', 'Bukalapak', 'Lainnya'].map(mp => (
                                <SelectItem key={mp} value={mp}>{mp}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Input
                            value={link.url}
                            onChange={(e) => {
                              const updated = [...marketplaceLinks];
                              updated[index] = { ...updated[index], url: e.target.value };
                              setMarketplaceLinks(updated);
                            }}
                            placeholder="https://shopee.co.id/toko-anda"
                            className="flex-1 rounded-xl bg-slate-50 dark:bg-zinc-950/60 border-slate-200 dark:border-zinc-800"
                          />
                          <Button variant="ghost" size="icon" onClick={() => setMarketplaceLinks(marketplaceLinks.filter((_, i) => i !== index))}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      ))}
                      <Button variant="outline" size="sm" onClick={() => setMarketplaceLinks([...marketplaceLinks, { platform: 'Shopee', url: '' }])} className="gap-1 mt-1">
                        <Plus className="w-3 h-3" /> Tambah Link
                      </Button>
                    </div>
                  )}
                </div>

                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400 mb-5 flex items-center gap-2">
                    <Palette className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    {t('aiAgentDetail.widgetAppearance')}
                  </h2>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.primaryColor')}</Label>
                      <div className="mt-2 flex items-center gap-3">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="h-11 w-11 shrink-0 cursor-pointer rounded-xl border border-slate-200 bg-transparent dark:border-zinc-800"
                          data-testid="color-picker"
                        />
                        <Input
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                          className="flex-1 rounded-xl bg-slate-50 font-mono text-sm uppercase dark:bg-zinc-950/60 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                        />
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="color-presets">
                        {['#10B981', '#6366F1', '#0EA5E9', '#F59E0B', '#EF4444', '#8B5CF6'].map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => setPrimaryColor(preset)}
                            title={preset}
                            aria-label={preset}
                            className={`h-7 w-7 rounded-lg transition-transform hover:scale-110 ${
                              primaryColor.toUpperCase() === preset
                                ? 'ring-2 ring-offset-2 ring-indigo-500 ring-offset-white dark:ring-offset-zinc-900'
                                : ''
                            }`}
                            style={{ backgroundColor: preset }}
                          />
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.welcomeMessage')}</Label>
                      <Textarea
                        value={welcomeMessage}
                        onChange={(e) => setWelcomeMessage(e.target.value)}
                        className="mt-2 rounded-xl bg-slate-50 dark:bg-zinc-950/60 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                        rows={2}
                        data-testid="welcome-msg-input"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400 mb-5 flex items-center gap-2">
                    <Globe className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    {t('aiAgentDetail.language')}
                  </h2>
                  <Select value={botLanguage} onValueChange={setBotLanguage}>
                    <SelectTrigger data-testid="language-select" className="rounded-xl bg-slate-50 dark:bg-zinc-950/60 dark:text-zinc-50 border-slate-200 dark:border-zinc-800">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-zinc-900 dark:border-zinc-800">
                      <SelectItem value="id" className="dark:text-zinc-50 dark:focus:bg-slate-700">{t('aiAgentDetail.languageId')}</SelectItem>
                      <SelectItem value="en" className="dark:text-zinc-50 dark:focus:bg-slate-700">{t('aiAgentDetail.languageEn')}</SelectItem>
                      <SelectItem value="both" className="dark:text-zinc-50 dark:focus:bg-slate-700">{t('aiAgentDetail.languageBoth')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </TabsContent>

              <TabsContent value="prompt" className="space-y-6">
                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                      {t('aiAgentDetail.systemPrompt')}
                    </h2>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      agentType === 'cs' 
                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300' 
                        : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                    }`} data-testid="prompt-agent-type-badge">
                      {agentType === 'cs' ? 'CS Agent' : 'Sales Agent'}
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 mb-4">
                    {t('aiAgentDetail.systemPromptDesc')}
                  </p>
                  <Textarea
                    value={systemPrompt}
                    onChange={(e) => setSystemPrompt(e.target.value)}
                    rows={8}
                    placeholder={t('aiAgentDetail.systemPromptPlaceholder')}
                    className="rounded-xl bg-slate-50 font-mono text-sm leading-relaxed dark:bg-zinc-950/60 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                    data-testid="system-prompt-input"
                  />
                  {(() => {
                    const chars = (systemPrompt || '').length;
                    const tokens = Math.round(chars / 3.5);
                    const level = chars <= 1500 ? 'ok' : chars <= 3000 ? 'warn' : 'high';
                    const styles = {
                      ok: { bar: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' },
                      warn: { bar: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400' },
                      high: { bar: 'bg-red-500', text: 'text-red-600 dark:text-red-400' },
                    }[level];
                    const message = level === 'ok'
                      ? t('aiAgentDetail.promptSizeIdeal')
                      : level === 'warn' ? t('aiAgentDetail.promptSizeWarn') : t('aiAgentDetail.promptSizeHigh');
                    return (
                      <div className="mt-3" data-testid="prompt-size-indicator">
                        <div className="flex items-center justify-between text-xs mb-1.5">
                          <span className="text-slate-500 dark:text-zinc-400">
                            {t('aiAgentDetail.promptSizeLabel')}
                          </span>
                          <span className={`font-medium tabular-nums ${styles.text}`} data-testid="prompt-size-count">
                            {chars.toLocaleString('id-ID')} {t('aiAgentDetail.promptSizeChars')} · ≈{tokens.toLocaleString('id-ID')} {t('aiAgentDetail.promptSizeTokens')}
                          </span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-zinc-800 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-[width] duration-300 ${styles.bar}`}
                            style={{ width: `${Math.min(100, (chars / 3000) * 100)}%` }}
                          />
                        </div>
                        <p className={`mt-2 text-xs leading-relaxed ${styles.text}`} data-testid="prompt-size-message">
                          {message}
                        </p>
                        <p className="mt-1 text-xs text-slate-500 dark:text-zinc-500">
                          {t('aiAgentDetail.promptSizeHint')}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              </TabsContent>

              <TabsContent value="faq" className="space-y-6">
                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50">{t('aiAgentDetail.faqTitle')}</h2>
                      <p className="text-sm text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.faqDesc')}</p>
                    </div>
                    <Button onClick={addFaq} variant="outline" className="rounded-xl dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800" data-testid="add-faq-btn">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('aiAgentDetail.addFaq')}
                    </Button>
                  </div>

                  {faqs.length === 0 ? (
                    <div className="text-center py-8 text-slate-500 dark:text-zinc-400">
                      {t('aiAgentDetail.noFaq')}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {faqs.map((faq, index) => (
                        <div key={index} className="rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-zinc-800 dark:bg-zinc-950/60" data-testid={`faq-item-${index}`}>
                          <div className="flex justify-between mb-3">
                            <span className="text-sm font-medium text-slate-700 dark:text-zinc-300">{t('aiAgentDetail.faqNumber')}{index + 1}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeFaq(index)}
                              className="text-red-500 hover:text-red-600 dark:hover:bg-zinc-900 h-8 w-8 p-0 dark:text-red-400"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <Label className="text-xs dark:text-zinc-300">{t('aiAgentDetail.question')}</Label>
                              <Input
                                value={faq.question}
                                onChange={(e) => updateFaq(index, 'question', e.target.value)}
                                placeholder={t('aiAgentDetail.questionPlaceholder')}
                                className="mt-1 rounded-xl bg-white dark:bg-zinc-900 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                              />
                            </div>
                            <div>
                              <Label className="text-xs dark:text-zinc-300">{t('aiAgentDetail.answer')}</Label>
                              <Textarea
                                value={faq.answer}
                                onChange={(e) => updateFaq(index, 'answer', e.target.value)}
                                placeholder={t('aiAgentDetail.answerPlaceholder')}
                                className="mt-1 rounded-xl bg-white dark:bg-zinc-900 dark:text-zinc-50 border-slate-200 dark:border-zinc-800 focus-visible:ring-indigo-500/40"
                                rows={2}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </TabsContent>


              <TabsContent value="knowledge" className="space-y-6">
                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-zinc-50 mb-2 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                        Knowledge Base
                      </h2>
                      <p className="text-sm text-slate-500 dark:text-zinc-400">
                        Upload dokumen sebagai sumber pengetahuan Agent Anda
                      </p>
                    </div>
                  </div>

                  {/* Plan Info */}
                  {planLimits && (
                    <div className="bg-gradient-to-r from-blue-50 to-blue-50 dark:from-blue-900/20 dark:to-blue-900/20 rounded-xl p-4 mb-6 border border-indigo-200 dark:border-indigo-800">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm font-semibold text-slate-900 dark:text-zinc-50 mb-1">
                            Batasan Plan {planLimits.plan.toUpperCase()}
                          </p>
                          <ul className="text-sm text-slate-700 dark:text-zinc-300 space-y-1">
                            <li>• Maksimal {planLimits.plan === 'trial' ? '1 file (2MB)' : '5 files (5MB each)'}</li>
                            <li>• Format: PDF, DOCX, XLSX, CSV, TXT</li>
                            <li>• Saat ini: {knowledgeFiles.length} dari {planLimits.plan === 'trial' ? '1' : '5'} file</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Upload Area */}
                  <div className="mb-6">
                    <label 
                      htmlFor="file-upload" 
                      className={`block w-full border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                        uploadingFile 
                          ? 'border-indigo-300 bg-indigo-50 dark:border-blue-700 dark:bg-indigo-950/30' 
                          : 'border-slate-300 dark:border-zinc-700 hover:border-blue-500 dark:hover:border-blue-500 hover:bg-slate-50 dark:hover:bg-zinc-800/50'
                      }`}
                    >
                      <input
                        id="file-upload"
                        type="file"
                        className="hidden"
                        accept=".pdf,.docx,.xlsx,.xls,.csv,.txt"
                        onChange={handleFileUpload}
                        disabled={uploadingFile || (planLimits && knowledgeFiles.length >= (planLimits.plan === 'trial' ? 1 : 5))}
                      />
                      <Upload className={`w-12 h-12 mx-auto mb-3 ${uploadingFile ? 'text-indigo-600 animate-bounce' : 'text-slate-500 dark:text-zinc-400'} dark:text-indigo-400`} />
                      <p className="font-semibold text-slate-900 dark:text-zinc-50 mb-1">
                        {uploadingFile ? 'Mengupload & memproses file...' : 'Klik untuk upload file'}
                      </p>
                      <p className="text-sm text-slate-500 dark:text-zinc-400">
                        PDF, DOCX, XLSX, CSV, TXT (Max {planLimits?.plan === 'trial' ? '2MB' : '5MB'})
                      </p>
                    </label>
                  </div>

                  {/* Files List */}
                  {knowledgeFiles.length > 0 ? (
                    <div className="space-y-3">
                      <h3 className="font-semibold text-slate-900 dark:text-zinc-50 text-sm mb-3">
                        File yang telah di-upload ({knowledgeFiles.length})
                      </h3>
                      {knowledgeFiles.map((file) => (
                        <div
                          key={file.id}
                          className="group flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 p-4 transition-colors hover:border-indigo-300 dark:border-zinc-800 dark:bg-zinc-950/60 dark:hover:border-zinc-700"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-950/40 flex items-center justify-center flex-shrink-0">
                              <File className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-900 dark:text-zinc-50 truncate">
                                {file.filename}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-zinc-400 mt-1">
                                <span>{formatFileSize(file.size)}</span>
                                <span>•</span>
                                <span>{new Date(file.uploaded_at).toLocaleDateString('id-ID', { timeZone: 'Asia/Jakarta' })}</span>
                              </div>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteFile(file.id)}
                            className="text-slate-500 hover:text-red-600 dark:text-zinc-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 py-8 text-center dark:border-zinc-800 dark:bg-zinc-950/60">
                      <File className="w-12 h-12 text-slate-500 dark:text-zinc-500 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 dark:text-zinc-400">
                        Belum ada file yang di-upload
                      </p>
                    </div>
                  )}

                  {/* Info Box */}
                  <div className="mt-6 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-200 dark:border-amber-800">
                    <div className="flex gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-amber-900 dark:text-amber-200">
                        <p className="font-semibold mb-1"><Lightbulb className="w-4 h-4 inline mr-1" />Tips Penggunaan:</p>
                        <ul className="space-y-1 text-amber-800 dark:text-amber-300">
                          <li>• AI akan menggunakan isi dokumen untuk menjawab pertanyaan</li>
                          <li>• Upload dokumen yang relevan dengan bisnis/layanan Anda</li>
                          <li>• Kombinasikan dengan FAQ untuk hasil optimal</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="embed" className="space-y-6">
                <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 p-5 sm:p-6 transition-colors hover:border-slate-300 dark:hover:border-zinc-700">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-zinc-400 mb-5 flex items-center gap-2">
                    <Code className="w-4 h-4 text-indigo-500 dark:text-indigo-400" />
                    {t('aiAgentDetail.embedCode')}
                  </h2>
                  <p className="text-sm text-slate-500 dark:text-zinc-400 mb-4">
                    {t('aiAgentDetail.embedDesc')}
                  </p>
                  
                  {!embedCode ? (
                    <Button
                      onClick={generateEmbedCode}
                      className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl"
                      disabled={generatingEmbed}
                      data-testid="generate-embed-btn"
                    >
                      {generatingEmbed ? t('aiAgentDetail.generating') : t('aiAgentDetail.generateEmbed')}
                    </Button>
                  ) : (
                    <div>
                      <div className="relative w-full">
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-xl overflow-x-auto text-xs sm:text-sm max-w-full">
                          <code className="break-all whitespace-pre-wrap">{embedCode}</code>
                        </pre>
                        <Button
                          onClick={copyEmbedCode}
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 text-slate-500 hover:text-white dark:text-zinc-400"
                          data-testid="copy-embed-btn"
                        >
                          {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                        </Button>
                      </div>
                      <Button
                        onClick={generateEmbedCode}
                        variant="outline"
                        className="w-full mt-4 rounded-xl dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-zinc-800"
                        disabled={generatingEmbed}
                      >
                        {t('aiAgentDetail.regenerate')}
                      </Button>
                    </div>
                  )}
                </div>
                {/* <EmbedCodeBox
                  embedCode={embedCode}
                  generateEmbedCode={generateEmbedCode}
                  copyEmbedCode={copyEmbedCode}
                  generatingEmbed={generatingEmbed}
                  copied={copied}
                  t={t}
                /> */}
              </TabsContent>
            </Tabs>
          </div>

          {/* Preview Panel */}
          <div className="lg:sticky lg:top-24 h-fit">
            <div className="bg-white dark:bg-zinc-900/80 rounded-2xl border border-slate-200 dark:border-zinc-800 overflow-hidden">
              <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-slate-100 dark:border-zinc-800">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="grid h-9 w-9 shrink-0 place-items-center rounded-xl"
                    style={{ backgroundColor: primaryColor + '1f' }}
                  >
                    <Bot className="h-4 w-4" style={{ color: primaryColor }} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-semibold text-slate-900 dark:text-zinc-50">
                      {t('aiAgentDetail.previewChat')}
                    </h2>
                    <p className="truncate text-xs text-slate-500 dark:text-zinc-400">{t('aiAgentDetail.previewDesc')}</p>
                  </div>
                </div>
                {previewMessages.length > 1 && (
                  <Button
                    onClick={clearChatHistory}
                    variant="ghost"
                    size="sm"
                    className="h-8 shrink-0 px-2 text-slate-500 hover:text-red-600 dark:text-zinc-400 dark:hover:text-red-400"
                    title={language === 'id' ? 'Bersihkan riwayat chat' : 'Clear chat history'}
                    data-testid="clear-preview-chat-btn"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              
              <div className="h-[420px] lg:h-[calc(100vh-26rem)] lg:min-h-[380px] bg-slate-50 dark:bg-[#0A0A0A] p-4 overflow-y-auto">
                <div className="space-y-4">
                  {previewMessages.map((msg, index) => (
                    <div
                      key={index}
                      className={`max-w-[85%] p-3 rounded-2xl shadow-sm ${
                        msg.type === 'user'
                          ? 'ml-auto text-white rounded-br-md'
                          : 'bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 dark:text-zinc-50 rounded-bl-md'
                      }`}
                      style={msg.type === 'user' ? { backgroundColor: primaryColor } : {}}
                    >
                      <p className="text-sm whitespace-pre-wrap">{msg.text}</p>
                      {/* Render product images if available */}
                      {msg.images && msg.images.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {msg.images.map((img, imgIndex) => {
                            const proxyUrl = `${process.env.REACT_APP_BACKEND_URL}/api/image-proxy?url=${encodeURIComponent(img.url)}`;
                            return (
                            <div key={imgIndex} className="rounded-lg overflow-hidden border border-slate-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
                              <div className="relative">
                                <img 
                                  src={proxyUrl} 
                                  alt={img.name || 'Product'} 
                                  className="w-full max-h-48 object-contain bg-slate-50 dark:bg-zinc-900"
                                  onError={(e) => { 
                                    // Fallback: try direct URL if proxy fails
                                    if (e.target.src !== img.url) {
                                      e.target.src = img.url;
                                      return;
                                    }
                                    e.target.style.display = 'none';
                                    const fallback = e.target.nextElementSibling;
                                    if (fallback) fallback.style.display = 'flex';
                                  }}
                                />
                                <div className="hidden w-full h-32 bg-slate-100 dark:bg-zinc-900 items-center justify-center flex-col gap-1">
                                  <ImageIcon className="w-8 h-8 text-slate-500 dark:text-zinc-500" />
                                  <span className="text-xs text-slate-500 dark:text-zinc-400">Gambar tidak tersedia</span>
                                </div>
                              </div>
                              <div className="p-2">
                                <p className="text-xs font-medium text-slate-900 dark:text-zinc-50">{img.name || 'Product'}</p>
                                {img.price > 0 && (
                                  <p className="text-xs font-semibold" style={{ color: primaryColor }}>
                                    Rp {Number(img.price).toLocaleString('id-ID')}
                                  </p>
                                )}
                              </div>
                            </div>
                          )})}
                        </div>
                      )}
                    </div>
                  ))}
                  {chatting && (
                    <div className="max-w-[80%] p-3 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-800 rounded-2xl rounded-bl-md">
                      <div className="flex gap-1">
                        <div className="w-2 h-2 bg-slate-300 dark:bg-zinc-700 rounded-full animate-bounce"></div>
                        <div className="w-2 h-2 bg-slate-300 dark:bg-zinc-700 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                        <div className="w-2 h-2 bg-slate-300 dark:bg-zinc-700 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      </div>
                    </div>
                  )}

                  {previewMessages.length <= 1 && !chatting && (
                    <div className="pt-4" data-testid="preview-suggestions">
                      <p className="mb-2 text-xs uppercase tracking-wide text-slate-400 dark:text-zinc-500">
                        {language === 'id' ? 'Coba tanya' : 'Try asking'}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {(language === 'id'
                          ? ['Ada produk apa saja?', 'Berapa harganya?', 'Cara pesannya gimana?']
                          : ['What products do you have?', 'How much is it?', 'How do I order?']
                        ).map((suggestion) => (
                          <button
                            key={suggestion}
                            type="button"
                            onClick={() => setPreviewInput(suggestion)}
                            className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-600 transition-colors hover:border-indigo-300 hover:text-indigo-700 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:border-indigo-500/50 dark:hover:text-indigo-300"
                          >
                            {suggestion}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="p-4 border-t border-slate-100 dark:border-zinc-800">
                <div className="flex items-center gap-2">
                  <Input
                    value={previewInput}
                    onChange={(e) => setPreviewInput(e.target.value)}
                    placeholder={t('aiAgentDetail.typePlaceholder')}
                    className="h-11 flex-1 rounded-full border-slate-200 bg-slate-50 px-4 dark:border-zinc-800 dark:bg-zinc-950/60 dark:text-zinc-50 focus-visible:ring-indigo-500/40"
                    onKeyPress={(e) => e.key === 'Enter' && sendPreviewMessage()}
                    data-testid="preview-chat-input"
                  />
                  <Button
                    onClick={sendPreviewMessage}
                    className="h-11 w-11 shrink-0 rounded-full p-0 transition-transform hover:-translate-y-0.5 disabled:opacity-60"
                    style={{ backgroundColor: primaryColor }}
                    disabled={chatting}
                    data-testid="preview-send-btn"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            <div
              className="mt-4 flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50/70 p-4 dark:border-red-900/40 dark:bg-red-950/20 sm:flex-row sm:items-center sm:justify-between"
              data-testid="danger-zone"
            >
              <div className="flex items-start gap-3">
                <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-red-100 text-red-600 dark:bg-red-500/15 dark:text-red-400">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-red-700 dark:text-red-300">
                    {language === 'id' ? 'Hapus AI Agent' : 'Delete AI Agent'}
                  </h3>
                  <p className="mt-0.5 text-xs leading-relaxed text-red-600/90 dark:text-red-400/80">
                    {language === 'id'
                      ? 'Semua percakapan dan file terkait ikut terhapus permanen.'
                      : 'All conversations and related files are permanently removed.'}
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                size="sm"
                className="shrink-0 gap-2 rounded-xl border-red-300 bg-transparent text-red-600 hover:bg-red-100 dark:border-red-800/60 dark:text-red-400 dark:hover:bg-red-900/30"
                onClick={handleDeleteChatbot}
                data-testid="delete-chatbot-btn"
              >
                <Trash2 className="h-4 w-4" />
                {language === 'id' ? 'Hapus' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ChatbotDetail;
